import React, { useState, useMemo, useEffect } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    FormControlLabel,
    List,
    styled,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import SearchInput from "../../../Overview/components/SearchInput";
import CustomCheckbox from "../../../Overview/components/Checkbox";
import { getFilterData } from "../../../../Api/Api";
import PrimaryChip from "../../../../Components/PrimaryChip";
import { PrimaryFormActionButton } from "../../../../Components/StyledComponent";

const ItemRowCheckBoxLabel = styled(FormControlLabel)(() => ({
    display: "block",
    marginLeft: 0,
    ".MuiButtonBase-root": { padding: 0 },
    ".MuiCheckbox-root": {
        padding: 0,
        color: "#A2A2A2 !important",
        "&.Mui-checked": { color: "#494949 !important" },
    },
    ".MuiSvgIcon-root": { width: "12px", height: "12px" },
}));

const CellChemistryFilter = ({ selectedCellChemistries = [], onCellChemistryChange }) => {
    const [search, setSearch] = useState("");
    const [tempSelected, setTempSelected] = useState(selectedCellChemistries);
    const [appliedSelected, setAppliedSelected] = useState(selectedCellChemistries);
    const [chemistries, setChemistries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchChemistries = async () => {
            try {
                setIsLoading(true);
                const filterConfig = {
                    type: "dynamic",
                    queryParams: { table: "batteryConfigList", column: "cellChemistry" },
                };
                const res = await getFilterData(filterConfig);
                const list = res?.response?.response?.filterData || [];
                setChemistries(list.map((item) => item.id));
            } catch (error) {
                console.error("Error fetching cell chemistries:", error);
                setChemistries([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchChemistries();
    }, []);

    const filteredOptions = useMemo(() => {
        if (!search.trim()) return chemistries;
        return chemistries.filter((item) =>
            item.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, chemistries]);

    const handleSelection = (option) => {
        setTempSelected((prev) =>
            prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
        );
    };

    const handleApply = () => {
        setAppliedSelected(tempSelected);
        if (onCellChemistryChange) onCellChemistryChange(tempSelected);
    };

    const handleChipDelete = (item) => {
        const newSelected = appliedSelected.filter((x) => x !== item);
        setAppliedSelected(newSelected);
        setTempSelected(newSelected);
        if (onCellChemistryChange) onCellChemistryChange(newSelected);
    };

    useEffect(() => {
        setTempSelected(selectedCellChemistries);
        setAppliedSelected(selectedCellChemistries);
    }, [selectedCellChemistries]);

    return (
        <Accordion
            sx={{
                boxShadow: "none",
                borderBottom: "1px solid #E6E6E6",
                my: 0,
                "&:before": { display: "none" },
                "&.Mui-expanded": { my: 0 },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    paddingTop: "18px",
                    paddingBottom: "18px",
                    alignItems: "flex-start",
                    "&.Mui-expanded": { minHeight: "auto" },
                    "& .MuiAccordionSummary-content": { margin: 0, alignItems: "flex-start", gap: 1 },
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography variant="fw400s14c000000" sx={{ lineHeight: "16px", color: "#1C1C1C", mr: 1 }}>
                        Cell Chemistry
                    </Typography>
                    {appliedSelected.length > 0 && (
                        <Box
                            sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 1 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {appliedSelected.map((item) => (
                                <PrimaryChip
                                    key={item}
                                    label={item}
                                    tooltipTitle={item}
                                    tooltipPlacement="bottom"
                                    onDelete={() => handleChipDelete(item)}
                                    deleteIcon={<CloseIcon />}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <SearchInput
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search Cell Chemistry"
                    />
                    <Box sx={{ maxHeight: "200px", overflowY: "auto" }}>
                        {isLoading ? (
                            <Typography variant="fw400s12lh24c3D4B5A" sx={{ textAlign: "center", py: 2 }}>
                                Loading chemistries...
                            </Typography>
                        ) : filteredOptions.length === 0 ? (
                            <Typography variant="fw400s12lh24c3D4B5A" sx={{ textAlign: "center", py: 2 }}>
                                No chemistries found
                            </Typography>
                        ) : (
                            <List disablePadding>
                                {filteredOptions.map((option, index) => (
                                    <Box key={index} sx={{ py: 0.5 }}>
                                        <ItemRowCheckBoxLabel
                                            control={
                                                <CustomCheckbox
                                                    checked={tempSelected.includes(option)}
                                                    onChange={() => handleSelection(option)}
                                                />
                                            }
                                            label={
                                                <Typography variant="fw400s12lh24c3D4B5A">{option}</Typography>
                                            }
                                        />
                                    </Box>
                                ))}
                            </List>
                        )}
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5, mt: 1 }}>
                        <Typography
                            variant="fw500s12lh24c6D72F6"
                            sx={{
                                cursor: tempSelected.length > 0 ? "pointer" : "not-allowed",
                                color: tempSelected.length > 0 ? "#6D72F6" : "#8F8F8F",
                            }}
                            onClick={() => tempSelected.length > 0 ? setTempSelected([]) : null}
                        >
                            Clear Selection
                        </Typography>
                        <PrimaryFormActionButton onClick={handleApply} sx={{ py: 0.75, px: 2, fontSize: "12px" }}>
                            Apply
                        </PrimaryFormActionButton>
                    </Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default CellChemistryFilter;
